import omitBy from "lodash/omitBy";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import {
	$deepPopulateItemMetadata,
	$getItemMetadata,
	$getOptionMetadata,
	$isOrderUnpaid,
	BASE_SUBTOTAL_ADDONS,
	sortByAscendingSubtotal,
} from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyAdjustmentToItem = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	item,
	adjustmentAmount,
}: {
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>;
	adjustmentAmount: number;
}) => {
	const orderItem = $getItemMetadata(item);
	const subtotal_addons = {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(orderItem.subtotal_addons, (value) => value === null),
		adjustment: {
			amount: adjustmentAmount,
		},
	};

	let orderItemOptions = orderItem.options.map((orderItemOption) =>
		$getOptionMetadata(orderItemOption, orderItem),
	);
	orderItemOptions = orderItemOptions.map((orderItemOption) => {
		const optionPriceRatioToItem = bigMath.div(
			orderItemOption.subtotal,
			orderItem.subtotal,
		);

		return {
			...orderItemOption,
			subtotal_addons: {
				...orderItemOption.subtotal_addons,
				adjustment: {
					amount: bigMath.mul(optionPriceRatioToItem, adjustmentAmount),
				},
			},
		};
	});

	return {
		...orderItem,
		subtotal_addons,
		options: orderItemOptions,
	};
};

export const applyAdjustmentToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	adjustmentAmount,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	adjustmentAmount: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	let remainingAmountForDivision = adjustmentAmount;
	orderItems = orderItems.sort(sortByAscendingSubtotal).map((orderItem, i) => {
		const isThereAmountToDivide = remainingAmountForDivision !== 0;
		const isThisLastItem = i === orderItems.length - 1;

		if (isThisLastItem) {
			const subtotal_addons = {
				...orderItem.subtotal_addons,
				adjustment: {
					amount: isThereAmountToDivide ? remainingAmountForDivision : 0,
				},
			};

			return { ...orderItem, subtotal_addons };
		}

		const itemPriceRatioToOrder = bigMath.div(orderItem.subtotal, subtotal);
		let dividedAdjustmentAmountForItem = isThereAmountToDivide
			? bigMath.mul(adjustmentAmount, itemPriceRatioToOrder)
			: 0;
		dividedAdjustmentAmountForItem =
			Math.abs(dividedAdjustmentAmountForItem) > orderItem.subtotal
				? orderItem.subtotal
				: dividedAdjustmentAmountForItem;

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			adjustment: {
				amount: dividedAdjustmentAmountForItem,
			},
		};

		remainingAmountForDivision = bigMath.sub(
			remainingAmountForDivision,
			dividedAdjustmentAmountForItem,
		);

		return { ...orderItem, subtotal_addons };
	});

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.subtotal,
				orderItem.subtotal,
			);

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					adjustment: {
						amount: bigMath.mul(
							optionPriceRatioToItem,
							orderItem.subtotal_addons.adjustment.amount,
						),
					},
				},
			};
		}),
	}));

	const adjustment_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.adjustment.amount),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		adjustment_amount,
	}).$order;
};

export const applyAdjustmentToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders: allOrders,
	adjustmentAmount,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	adjustmentAmount: number;
	skipPaidCheck?: boolean;
}) => {
	const unpaidOrders = allOrders.filter((o) =>
		skipPaidCheck ? true : $isOrderUnpaid(o),
	);
	const ordersGroupSubtotal = unpaidOrders.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);
	let remainingAmountForDivision = adjustmentAmount || 0;
	const updatedOrders = unpaidOrders
		// @ts-ignore
		.sort(sortByAscendingSubtotal)
		.map((order, i) => {
			const isThereAmountToDivide = remainingAmountForDivision !== 0;
			const isThisLastItem = i === unpaidOrders.length - 1;

			if (isThisLastItem) {
				return applyAdjustmentToOrder({
					order,
					adjustmentAmount: isThereAmountToDivide
						? remainingAmountForDivision
						: 0,
				});
			}

			const orderPriceRatioToGroup = bigMath.div(
				order.subtotal,
				ordersGroupSubtotal,
			);
			let dividedAdjustmentAmountForOrder = isThereAmountToDivide
				? bigMath.mul(adjustmentAmount, orderPriceRatioToGroup)
				: 0;
			dividedAdjustmentAmountForOrder =
				Math.abs(dividedAdjustmentAmountForOrder) > order.subtotal
					? order.subtotal
					: dividedAdjustmentAmountForOrder;

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedAdjustmentAmountForOrder,
			);

			return applyAdjustmentToOrder({
				order,
				adjustmentAmount: dividedAdjustmentAmountForOrder,
			});
		});

	return updatedOrders;
};
